import React, { useEffect, useState } from "react";
import api from "../api/client";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "appartement", label: "Appartement" },
  { value: "maison", label: "Maison" },
  { value: "studio", label: "Studio" },
  { value: "duplex", label: "Duplex" },
];

const CATEGORIES = [
  { value: "", label: "Standard" },
  { value: "confort", label: "Confort" },
  { value: "premium", label: "Premium" },
  { value: "luxe", label: "Luxe" },
];

/**
 * Configuration des étages pour générer les unités en masse.
 * Format : [{etage: 0, count: 2, type_unite: "appartement"}, ...]
 */
export default function EtagesConfigPanel({ immeuble, onUpdate }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setRows(immeuble.etages_config || []);
  }, [immeuble.id]);

  const addRow = () => setRows([...rows, { etage: 0, count: 1, type_unite: "appartement", categorie: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, key, val) => {
    const next = [...rows];
    if (key === "type_unite" || key === "categorie") {
      next[i] = { ...next[i], [key]: val };
    } else {
      next[i] = { ...next[i], [key]: Number(val) || 0 };
    }
    setRows(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/immeubles/${immeuble.id}`, { etages_config: rows });
      toast.success("Configuration enregistrée");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (rows.length === 0) {
      toast.error("Ajoutez au moins une ligne de configuration");
      return;
    }
    setGenerating(true);
    try {
      // Save first to ensure backend has the latest config
      await api.patch(`/immeubles/${immeuble.id}`, { etages_config: rows });
      const { data } = await api.post(`/immeubles/${immeuble.id}/generate-units`);
      if (data.created > 0) {
        toast.success(`${data.created} unité(s) générée(s)`);
      } else {
        toast.info("Aucune unité à générer (toutes existent déjà)");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const total = rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0);

  return (
    <div className="tpco-card overflow-hidden" data-testid="etages-config-panel">
      <div className="px-6 py-4 border-b border-[#E2E5F1] flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny">Configuration & génération</div>
          <h3 className="font-display text-lg font-bold">Unités par étage</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#4B5775]">Total prévu : <span className="font-bold text-[#0F1A3D]">{total}</span></span>
          <button
            type="button"
            onClick={addRow}
            data-testid="add-etage-row"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] hover:text-[#1E3A8A] text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-white border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E8EBFA] text-xs font-semibold"
          >
            {saving ? "…" : "Enregistrer"}
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={generating || rows.length === 0}
            data-testid="generate-units-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Wand2 className="w-3.5 h-3.5" /> {generating ? "Génération…" : "Générer les unités"}
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-[#9CA3B5]">
          Aucune configuration. Cliquez sur <strong>Ajouter</strong> pour définir le nombre d'unités attendu par étage,
          puis lancez la génération pour créer toutes les unités d'un coup.
        </div>
      ) : (
        <div className="divide-y divide-[#E2E5F1]">
          <div className="grid grid-cols-12 gap-3 px-6 py-2.5 bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">
            <div className="col-span-2">Étage</div>
            <div className="col-span-3">Type d'unité</div>
            <div className="col-span-3">Catégorie</div>
            <div className="col-span-2">Nombre</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 px-6 py-3 items-center" data-testid={`etage-row-${i}`}>
              <div className="col-span-2">
                <Input
                  type="number"
                  value={row.etage}
                  onChange={(e) => updateRow(i, "etage", e.target.value)}
                  className="h-9"
                  placeholder="0 = RDC"
                />
              </div>
              <div className="col-span-3">
                <Select value={row.type_unite} onValueChange={(v) => updateRow(i, "type_unite", v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Select value={row.categorie || "standard"} onValueChange={(v) => updateRow(i, "categorie", v === "standard" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value || "standard"} value={c.value || "standard"}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  min="1"
                  value={row.count}
                  onChange={(e) => updateRow(i, "count", e.target.value)}
                  className="h-9"
                  placeholder="Nb"
                />
              </div>
              <div className="col-span-2 text-right">
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E2E5F1] hover:border-[#C83B3B] hover:text-[#C83B3B] text-[#4B5775]"
                  aria-label="Supprimer la ligne"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
